import { useEffect, useState } from "react";

export type ResourceStatus = "loading" | "not-found" | "unavailable" | "error" | "ready";

export interface ApiResourceState<T> {
  status: ResourceStatus;
  data: T | null;
  message: string | null;
  // updatedAt is when `data` was last actually replaced by a successful
  // fetch (initial or background refresh) — client-observed, not anything
  // the backend reports. Its main consumer is a visible "Last updated"
  // note on a refreshIntervalMs-polling page (ProgressPage): without it,
  // there's no way for a user (or for debugging) to distinguish "the
  // refresh never fired" from "it fired, but the underlying data hadn't
  // changed" — both look identical on screen otherwise.
  updatedAt: Date | null;
}

// apiPath builds the request path for a repo-scoped API resource:
// /api/repos/{repo}{path}, e.g. apiPath("atlas", "/adrs") ->
// "/api/repos/atlas/adrs". The backend registers this alongside its
// original unprefixed routes (which now forward to the default/atlas
// repo) specifically so the frontend could migrate onto it independently —
// this is that migration. repo comes from useCurrentRepo() (the URL's own
// repo segment) in every real call site.
export function apiPath(repo: string, path: string): string {
  return `/api/repos/${repo}${path}`;
}

// useApiResource is the shared fetch+auth-header+loading-state pattern that
// was independently duplicated across every reader/index page in this app
// (CapabilityDetail, ADRIndex, ADRReader, RunbookIndex, RunbookReader,
// DesignIndex, DesignReader, BitsGallery, BitReader, DashboardStats,
// ProgressPage, CapabilityRegister) — this names it once rather than
// re-implementing it a 13th time. useSearch and useAuthedAssetURL are
// deliberately NOT built on this: useSearch has its own debounce semantics
// and is global (no repo), and useAuthedAssetURL returns a blob: URL, not
// parsed JSON — forcing either onto this shape would repeat the mistake
// this codebase's own ExtractADRMentions precedent already documents
// avoiding (don't force differently-shaped things into one abstraction).
export interface UseApiResourceOptions {
  // refreshIntervalMs, when set, re-fetches in the background on that
  // interval for as long as the component stays mounted — opt-in (default:
  // fetch once, matching every other consumer of this hook) since only
  // ProgressPage wants live updates today. A background refresh never
  // resets status to "loading" (the page keeps showing what it already
  // has) and a failed background refresh is silently ignored rather than
  // replacing good data with an error — only the initial fetch (and a
  // path/repo/token change) can transition into "loading"/"error"/etc.
  // Skipped while the tab isn't visible (document.visibilityState), so a
  // backgrounded tab doesn't keep generating GitHub-backed API calls. A
  // skipped tick is not lost, though: regaining visibility (a
  // "visibilitychange" listener, not just the next setInterval tick) fires
  // an immediate catch-up refresh if the interval has elapsed since the
  // last fetch. Without this, the common "leave the tab open, switch away,
  // come back later" pattern would show stale data indefinitely — every
  // tick while backgrounded is skipped, and browsers throttle background
  // timers besides, so the plain setInterval alone could go a long time
  // before the next check happens to land while the tab is visible again.
  refreshIntervalMs?: number;
}

export function useApiResource<T>(token: string, repo: string, path: string, options?: UseApiResourceOptions): ApiResourceState<T> {
  const [status, setStatus] = useState<ResourceStatus>("loading");
  const [data, setData] = useState<T | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const refreshIntervalMs = options?.refreshIntervalMs;

  useEffect(() => {
    let active = true;
    let lastFetchAt = Date.now();

    function load(isBackgroundRefresh: boolean) {
      lastFetchAt = Date.now();
      if (!isBackgroundRefresh) {
        setStatus("loading");
        setMessage(null);
      }
      fetch(apiPath(repo, path), { headers: { Authorization: `Bearer ${token}` } })
        .then(async (res) => {
          if (!active) return;
          if (!res.ok) {
            if (isBackgroundRefresh) return; // keep showing the last good data
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            setStatus(res.status === 404 ? "not-found" : res.status === 503 ? "unavailable" : "error");
            setMessage(body.error ?? `HTTP ${res.status}`);
            return;
          }
          const body = (await res.json()) as T;
          setData(body);
          setUpdatedAt(new Date());
          setStatus("ready");
        })
        .catch((e: unknown) => {
          if (!active || isBackgroundRefresh) return;
          setStatus("error");
          setMessage(e instanceof Error ? e.message : "unknown error");
        });
    }

    load(false);

    let intervalId: ReturnType<typeof setInterval> | undefined;
    function handleVisibilityChange() {
      if (document.visibilityState === "visible" && refreshIntervalMs && Date.now() - lastFetchAt >= refreshIntervalMs) {
        load(true);
      }
    }
    if (refreshIntervalMs) {
      intervalId = setInterval(() => {
        if (document.visibilityState === "visible") {
          load(true);
        }
      }, refreshIntervalMs);
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      active = false;
      if (intervalId !== undefined) clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [token, repo, path, refreshIntervalMs]);

  return { status, data, message, updatedAt };
}
